import type { UseApplyComponentActionType } from "@editor/hooks/useApplyComponentAction";
import type { Component, CustomPropDefinition } from "schemas/component";

import * as React from "react";

import { getCustomComponentPropTypeEditorData } from "@components/editor/customComponentProps";
import { Group } from "@editor/components/common/designSystem/Group";
import useApplyComponentAction from "@editor/hooks/useApplyComponentAction";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import useSetDraftElement from "@editor/hooks/useSetDraftElement";
import { useStoreProductsFromDraftElement } from "@editor/hooks/useStoreProducts";
import { selectLocaleData } from "@editor/reducers/commerce-reducer";
import {
  selectAllTextComponentNames,
  selectComponentDataMapping,
  selectDraftComponent,
  selectDraftComponentContext,
  selectGetAttribute,
} from "@editor/reducers/core-reducer";
import { selectTemplateEditorStoreProduct } from "@editor/reducers/template-reducer";
import { useEditorSelector } from "@editor/store";
import {
  getExtraActionsBasedOnComponentType,
  getSelectableOptionValues,
} from "@editorModifiers/utils";

import Button from "@replo/design-system/components/button/Button";
import { Info } from "lucide-react";
import { getCustomPropDefinitions } from "replo-runtime/shared/utils/component";

const CustomPropModifier: React.FC = () => {
  const draftComponent = useEditorSelector(selectDraftComponent);

  const context = useEditorSelector(selectDraftComponentContext);
  const allTextComponents = useEditorSelector(selectAllTextComponentNames);

  const getAttribute = useEditorSelector(selectGetAttribute);
  const applyComponentAction = useApplyComponentAction();
  const {
    activeCurrency,
    activeLanguage,
    moneyFormat,
    moneyWithCurrencyFormat,
  } = useEditorSelector(selectLocaleData);
  const { products } = useStoreProductsFromDraftElement();
  const { user } = useCurrentUser();
  const templateProduct =
    useEditorSelector(selectTemplateEditorStoreProduct) ?? null;
  if (!draftComponent) {
    return null;
  }

  const renderCustomProp = (customPropDefinition: CustomPropDefinition) => {
    const editorData = getCustomComponentPropTypeEditorData(
      customPropDefinition.type,
    );
    const { value } = getAttribute(
      draftComponent,
      `props.${customPropDefinition.id}`,
      {
        defaultValue: customPropDefinition.defaultValue,
      },
    );

    const onChange = (value: any) => {
      const actions: UseApplyComponentActionType[] = [
        {
          componentId: draftComponent.id,
          type: "setProps",
          value: { [customPropDefinition.id]: value },
        },
      ];

      const extraActionsBasedOnComponentType =
        getExtraActionsBasedOnComponentType(
          value,
          draftComponent,
          customPropDefinition,
        );

      applyComponentAction({
        type: "applyCompositeAction",
        value: [...actions, ...extraActionsBasedOnComponentType],
      });
    };

    const onDelete = () => {
      applyComponentAction({
        type: "deleteProps",
        propName: customPropDefinition.id,
      });
    };

    if (!editorData?.shouldRender(draftComponent)) {
      return null;
    }

    let isEnabled = true;
    if (
      customPropDefinition.isEnabled &&
      !customPropDefinition.isEnabled({ component: draftComponent })
    ) {
      isEnabled = false;
    }

    let shouldDisplay = true;
    if (
      customPropDefinition.shouldDisplay &&
      !customPropDefinition.shouldDisplay({
        component: draftComponent,
        userIsSuperuser: user?.isSuperuser ?? false,
      })
    ) {
      shouldDisplay = false;
    }

    const options = getSelectableOptionValues({
      customPropDefinition,
      draftComponent,
      context,
      productResolutionDependencies: {
        products,
        currencyCode: activeCurrency,
        language: activeLanguage,
        moneyFormat,
        moneyWithCurrencyFormat,
        templateProduct,
        isEditor: true,
        isShopifyProductsLoading: false,
      },
      allTextComponents,
    });

    const contentToRender = editorData?.render({
      component: draftComponent,
      products,
      value,
      customPropId: customPropDefinition.id,
      description: customPropDefinition.description ?? "",
      onChange,
      onDelete,
      options,
      attributeName: customPropDefinition.id,
      isEnabled,
      disabledDescription: customPropDefinition.disabledDescription,
      definition: customPropDefinition,
      context,
      defaultValue: customPropDefinition.defaultValue,
      disableDynamicData: customPropDefinition.disableDynamicData,
      templateProduct,
      placeholder: customPropDefinition.placeholder,
    });

    if (!shouldDisplay) {
      return null;
    }

    if (editorData.hasCustomWrapper) {
      return contentToRender;
    }

    return (
      <Group
        name={customPropDefinition.name}
        isDefaultOpen
        isCollapsible
        key={customPropDefinition.id}
      >
        <div className="w-full">{contentToRender}</div>
      </Group>
    );
  };

  const customProps = getCustomPropDefinitions(draftComponent);

  return (
    <div className="flex flex-col gap-4">
      {draftComponent.type === "tooltipContent" && (
        <TooltipContentInformation component={draftComponent} />
      )}
      {customProps
        .filter((definition) => definition.type !== "component")
        .map((customPropDefinition) => renderCustomProp(customPropDefinition))}
    </div>
  );
};

// TODO (REPL-16947 Gabe 2025-04-15): This should not be defined here. If we
// like this pattern of being able to show information about a component
// alongside prop modifiers then this should be configurable via component's
// config.
const TooltipContentInformation: React.FC<{ component: Component }> = ({
  component,
}) => {
  const componentData = useEditorSelector(selectComponentDataMapping)[
    component.id
  ];
  const setDraftElement = useSetDraftElement();
  return (
    <div className="bg-sheet text-muted flex gap-2 rounded-lg p-2 text-sm">
      <Info size={12} className="mt-0.5 shrink-0" />
      <div>
        Edit tooltip configurations on the parent component.
        <div className="text-primary underline">
          <Button
            variant="inherit"
            size="sm"
            onClick={() => {
              if (componentData?.parentId) {
                setDraftElement({ componentIds: [componentData.parentId] });
              }
            }}
          >
            <span>Go To Parent</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomPropModifier;
